import { Rule } from "@fairfleet/geotab";
import { GeotabCall } from "./fetchExceptionEvents";
import { RESULTS_LIMIT_RULE } from "@/constants/resultsLimits";
import {
  buildZoneVisitRule,
  ZONELOG_GROUP_NAME,
  ZONEVISIT_RULE_NAME_SEARCH,
  ZoneTypeRef,
  zoneTypeIdOfRule,
} from "@/pages/zone-report/utils/zoneLogRules";

export interface SyncZoneLogRulesInput {
  servicesGroupId: string;
  /** Missing → the group is created under Fairfleet Services first. */
  zonelogsGroupId?: string;
  types: ZoneTypeRef[];
}

/**
 * Adds per `ExecuteMultiCall`. `Add` is not batched by the client on its own; each entry still counts against
 * Geotab's per-minute limit, which the client's rate limiter (900/min) accounts for entry by entry.
 */
const RULE_ADD_BUNDLE = 100;

const ZONELOG_GROUP_COMMENT =
  "Fahrzeuge in dieser Gruppe werden in der Zonenauswertung erfasst (Regeln „[ZN] <Zonentyp>“). Nicht löschen.";

/** Only our rules, found by Geotab's name search. */
export async function readZoneLogRules(call: GeotabCall, signal?: AbortSignal, limit = RESULTS_LIMIT_RULE): Promise<Rule[]> {
  const rules = (await call(
    "Get",
    { typeName: "Rule", search: { name: ZONEVISIT_RULE_NAME_SEARCH }, resultsLimit: limit },
    signal,
  )) as Rule[];
  if (rules.length >= limit) {
    throw new Error(`More than ${limit} zone visit rules: the rest cannot be read, and creating them again would duplicate them.`);
  }
  return rules.filter(rule => zoneTypeIdOfRule(rule) !== undefined);
}

/**
 * Creates the missing zone visit rules (one per zone type) in bundles. The rules are read again right before, so a rule created
 * meanwhile (another tab, another user) is not created twice. A failed bundle stops the run; the bundles before
 * it exist and are found by the next read. Never changes or removes a rule.
 */
export async function syncZoneLogRules(
  call: GeotabCall,
  { servicesGroupId, zonelogsGroupId, types }: SyncZoneLogRulesInput,
  onProgress?: (done: number, total: number) => void,
): Promise<{ created: number; skipped: number }> {
  const groupId =
    zonelogsGroupId ??
    ((await call("Add", {
      typeName: "Group",
      entity: { name: ZONELOG_GROUP_NAME, parent: { id: servicesGroupId }, comment: ZONELOG_GROUP_COMMENT, children: [] },
    })) as string);

  const existing = new Set((await readZoneLogRules(call)).map(rule => zoneTypeIdOfRule(rule)));
  const todo = types.filter((type, index) => !existing.has(type.id) && types.findIndex(t => t.id === type.id) === index);
  onProgress?.(0, todo.length);

  for (let start = 0; start < todo.length; start += RULE_ADD_BUNDLE) {
    const bundle = todo.slice(start, start + RULE_ADD_BUNDLE);
    await call("ExecuteMultiCall", {
      calls: bundle.map(type => ({ method: "Add", params: { typeName: "Rule", entity: buildZoneVisitRule(type, groupId) } })),
    });
    onProgress?.(start + bundle.length, todo.length);
  }
  return { created: todo.length, skipped: types.length - todo.length };
}
